import { useForm } from "react-hook-form";
import { Form, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useMutation } from "@tanstack/react-query";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2Icon } from "lucide-react";
import { useState } from "react";
import { Input } from "./Input";
import { request } from "@/lib/request";
import { captureEvent, sanitizeErrorReason } from "@/lib/analytics";

export type AuthenticateForm = {
  email: string;
};

export const AuthenticationForget = ({
  goToLogin,
}: {
  goToLogin: () => void;
}) => {
  const [isSuccess, setIsSuccess] = useState(false);

  const { isPending, isError, error, mutate } = useMutation({
    mutationKey: ["login/register"],
    mutationFn: async ({ email }: AuthenticateForm) => {
      const res = await request
        .post(`/auth/forgot-password`, { email })
        .catch(err => {
          throw new Error(
            err.response?.data.message
              ? err.response?.data.message
              : "Something went wrong, please try again.",
          );
        });

      return res.data.data;
    },
    onSuccess: () => {
      captureEvent("onboarding_forgot_password_succeeded", {
        method: "email",
      });
      setIsSuccess(true);
    },
    onError: err => {
      captureEvent("onboarding_forgot_password_failed", {
        method: "email",
        reason: sanitizeErrorReason(err),
      });
    },
  });

  const form = useForm<AuthenticateForm>({
    defaultValues: {
      email: "",
    },
  });

  const handleSubmit = (values: AuthenticateForm) => {
    captureEvent("onboarding_forgot_password_submitted", {
      method: "email",
    });
    mutate({ ...values });
  };

  return isSuccess ? (
    <div className="flex flex-grow flex-col">
      <Alert className="border-none p-0">
        <AlertTitle className="text-lg font-bold">Success</AlertTitle>
        <AlertDescription className="text-lg">
          A password reset link has been sent to your email.
        </AlertDescription>
      </Alert>
    </div>
  ) : (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-grow flex-col justify-between"
      >
        <div className="flex flex-grow flex-col gap-5">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  placeholder="Enter your email..."
                  {...field}
                />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-grow flex-col justify-end gap-4">
          {isError ? (
            <Alert variant="destructive" className="-mb-8 border-none p-0">
              <AlertDescription>
                {error?.message || "An error occurred, please try again."}
              </AlertDescription>
            </Alert>
          ) : null}
          <Button
            type="submit"
            variant="default"
            disabled={isPending}
            className="mt-6 h-auto rounded-xl py-3"
          >
            {isPending ? (
              <>
                <Loader2Icon className="mr-2 h-5 w-5 animate-spin" />
                Loading...
              </>
            ) : (
              "Reset Password"
            )}
          </Button>
          <Button
            type="button"
            variant="link"
            className="-mt-2"
            onClick={goToLogin}
          >
            Back to Login
          </Button>
        </div>
      </form>
    </Form>
  );
};
